import { DiagLogLevel, SpanStatusCode, trace } from "@opentelemetry/api";
import { SeverityNumber, logs } from "@opentelemetry/api-logs";
import { OTLPMetricExporter } from "@opentelemetry/exporter-metrics-otlp-http";
import { MeterProvider, PeriodicExportingMetricReader } from "@opentelemetry/sdk-metrics";

//#region src/logs.ts
/**
* Helper to emit a log to OpenTelemetry
* This can be called manually from your code when you want to send logs to OTEL
*/
function emitOtelLog(level, context, message, attributes) {
	const otelLogger = logs.getLogger(context);
	const getSeverity = (level) => {
		switch (level.toLowerCase()) {
			case "debug": return SeverityNumber.DEBUG;
			case "verbose": return SeverityNumber.TRACE;
			case "log":
			case "info": return SeverityNumber.INFO;
			case "warn": return SeverityNumber.WARN;
			case "error": return SeverityNumber.ERROR;
			default: return SeverityNumber.INFO;
		}
	};
	const spanContext = trace.getActiveSpan()?.spanContext();
	const traceId = spanContext?.traceId;
	const spanId = spanContext?.spanId;
	otelLogger.emit({
		severityNumber: getSeverity(level),
		severityText: level.toUpperCase(),
		body: message,
		attributes: {
			...attributes,
			trace_id: traceId,
			span_id: spanId
		}
	});
}

//#endregion
//#region src/metrics.ts
const meter = new MeterProvider({ readers: [new PeriodicExportingMetricReader({
	exporter: new OTLPMetricExporter({}),
	exportIntervalMillis: 250
})] }).getMeter("@tg-search/observability");
/**
* WebSocket send fail total
*/
const wsSendFailTotal = meter.createCounter("ws.send.fail.total", { description: "Total number of failed WebSocket sends from server to client" });
/**
* WebSocket connections active gauge
*/
const wsConnectionsActive = meter.createUpDownCounter("ws.connections.active", { description: "Number of active WebSocket connections" });
/**
* Core events in total
*/
const coreEventsInTotal = meter.createCounter("core.events.in.total", { description: "Total number of events sent from client to core" });
/**
* Core messages processed total
*/
const coreMessagesProcessedTotal = meter.createCounter("core.messages.processed.total", { description: "Total number of messages processed by core message resolver" });
/**
* Core message batches processed total
*/
const coreMessageBatchesProcessedTotal = meter.createCounter("core.message.batches.processed.total", { description: "Total number of message batches processed by core message resolver" });
/**
* Core message batch duration histogram
*/
const coreMessageBatchDurationMs = meter.createHistogram("core.message.batch.duration.ms", {
	description: "Duration of message processing batches in milliseconds",
	unit: "ms"
});
/**
* Core resolver duration histogram
*/
const coreResolverDurationMs = meter.createHistogram("core.resolver.duration.ms", {
	description: "Duration of individual message resolvers in milliseconds",
	unit: "ms"
});
/**
* Core takeout messages downloaded total
*/
const coreTakeoutDownloadedTotal = meter.createCounter("core.takeout.downloaded.total", { description: "Total number of messages downloaded from Telegram via takeout" });
/**
* Core takeout page fetch total
*/
const coreTakeoutPageFetchTotal = meter.createCounter("core.takeout.page.fetch.total", { description: "Total number of pages fetched from Telegram API during takeout" });
/**
* Core takeout page fetch duration histogram
*/
const coreTakeoutPageFetchDurationMs = meter.createHistogram("core.takeout.page.fetch.duration.ms", {
	description: "Duration of individual page fetch calls in milliseconds",
	unit: "ms"
});
/**
* Core takeout session init total
*/
const coreTakeoutSessionInitTotal = meter.createCounter("core.takeout.session.init.total", { description: "Total number of takeout sessions initiated" });
/**
* Core takeout chat duration histogram
*/
const coreTakeoutChatDurationMs = meter.createHistogram("core.takeout.chat.duration.ms", {
	description: "Duration of per-chat takeout in milliseconds",
	unit: "ms"
});
/**
* Core takeout run total
*/
const coreTakeoutRunTotal = meter.createCounter("core.takeout.run.total", { description: "Total number of takeout runs started" });
/**
* Core resolver outcome total
*/
const coreResolverOutcomeTotal = meter.createCounter("core.resolver.outcome.total", { description: "Total number of resolver outcomes (success or error)" });
/**
* Core resolver skipped total
*/
const coreResolverSkippedTotal = meter.createCounter("core.resolver.skipped.total", { description: "Total number of resolvers skipped due to config or syncOptions" });
/**
* Core embedding API call total
*/
const coreEmbeddingApiCallTotal = meter.createCounter("core.embedding.api.call.total", { description: "Total number of embedding API calls" });
/**
* Core embedding tokens total
*/
const coreEmbeddingTokensTotal = meter.createCounter("core.embedding.tokens.total", { description: "Total number of embedding tokens consumed" });
/**
* Core media download total
*/
const coreMediaDownloadTotal = meter.createCounter("core.media.download.total", { description: "Total number of media download outcomes" });
/**
* Core vision API call total
*/
const coreVisionApiCallTotal = meter.createCounter("core.vision.api.call.total", { description: "Total number of vision LLM API calls" });
/**
* Core entity resolve total
*/
const coreEntityResolveTotal = meter.createCounter("core.entity.resolve.total", { description: "Total number of entity resolution outcomes" });
/**
* Core takeout page messages histogram
*/
const coreTakeoutPageMessages = meter.createHistogram("core.takeout.page.messages", { description: "Number of messages per takeout page fetch" });
/**
* Create OpenTelemetry counter from CoreCounter
*/
function createOtelCounter(otelCounter) {
	return { inc(labels, value) {
		otelCounter.add(value ?? 1, labels);
	} };
}
/**
* Create OpenTelemetry histogram from CoreHistogram
*/
function createOtelHistogram(otelHistogram) {
	return { observe(labels, value) {
		otelHistogram.record(value, labels);
	} };
}
/**
* Core metrics
*/
const coreMetrics = {
	messagesProcessed: createOtelCounter(coreMessagesProcessedTotal),
	messageBatchDuration: createOtelHistogram(coreMessageBatchDurationMs),
	resolverDuration: createOtelHistogram(coreResolverDurationMs),
	takeoutDownloadTotal: createOtelCounter(coreTakeoutDownloadedTotal),
	takeoutPageFetchTotal: createOtelCounter(coreTakeoutPageFetchTotal),
	takeoutPageFetchDurationMs: createOtelHistogram(coreTakeoutPageFetchDurationMs),
	takeoutSessionInitTotal: createOtelCounter(coreTakeoutSessionInitTotal),
	takeoutChatDurationMs: createOtelHistogram(coreTakeoutChatDurationMs),
	takeoutRunTotal: createOtelCounter(coreTakeoutRunTotal),
	resolverOutcome: createOtelCounter(coreResolverOutcomeTotal),
	resolverSkipped: createOtelCounter(coreResolverSkippedTotal),
	embeddingApiCall: createOtelCounter(coreEmbeddingApiCallTotal),
	embeddingTokens: createOtelCounter(coreEmbeddingTokensTotal),
	mediaDownload: createOtelCounter(coreMediaDownloadTotal),
	visionApiCall: createOtelCounter(coreVisionApiCallTotal),
	entityResolve: createOtelCounter(coreEntityResolveTotal),
	takeoutPageMessages: createOtelHistogram(coreTakeoutPageMessages)
};

//#endregion
//#region src/traces.ts
async function withSpan(spanName, fn, attributes) {
	return trace.getTracer("@tg-search/observability").startActiveSpan(spanName, async (span) => {
		if (attributes) span.setAttributes(attributes);
		try {
			return await fn();
		} catch (error) {
			span.recordException(error);
			span.setStatus({ code: SpanStatusCode.ERROR });
			throw error;
		} finally {
			span.end();
		}
	});
}

//#endregion
export { DiagLogLevel, coreEmbeddingApiCallTotal, coreEmbeddingTokensTotal, coreEntityResolveTotal, coreEventsInTotal, coreMediaDownloadTotal, coreMessageBatchDurationMs, coreMessageBatchesProcessedTotal, coreMessagesProcessedTotal, coreMetrics, coreResolverDurationMs, coreResolverOutcomeTotal, coreResolverSkippedTotal, coreTakeoutChatDurationMs, coreTakeoutDownloadedTotal, coreTakeoutPageFetchDurationMs, coreTakeoutPageFetchTotal, coreTakeoutPageMessages, coreTakeoutRunTotal, coreTakeoutSessionInitTotal, coreVisionApiCallTotal, emitOtelLog, withSpan, wsConnectionsActive, wsSendFailTotal };
//# sourceMappingURL=index.mjs.map